// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import cx from 'classnames';
import { MediaItemValue, Feature, VideoItemValue } from '@fulcrumapp/fulcrum-core';
import distance from '@turf/distance';
import filesize from 'filesize';
import ShareLink from '../../shared/share-link';
import 'location-origin';
import { AppContext } from '../../hooks/use-app';

export default class PhotoMetadataView extends React.Component {
  static propTypes = {
    className: PropTypes.string.isRequired,
    item: PropTypes.instanceOf(MediaItemValue).isRequired,
    feature: PropTypes.instanceOf(Feature),
    metadata: PropTypes.instanceOf(Object),
  };

  static contextType = AppContext;

  render() {
    const { styles } = PhotoMetadataView;

    return (
      <div className={cx(this.props.className, styles.container)}>
        {this.renderMetadata()}
      </div>
    );
  }

  renderMetadata() {
    const { styles } = PhotoMetadataView;

    if (this.props.metadata == null || this.props.item instanceof VideoItemValue) {
      return null;
    }

    return (
      <div>
        {this.context.params.data_share_id
          && (
            <div className={css(styles.block)}>
              <h1 className={css(styles.header)}>Share</h1>

              <div className={css(styles.fields)}>
                {this.renderShareLink()}
              </div>
            </div>
          )}

        {this.props.metadata && this.props.metadata.stored
          && (
            <div className={css(styles.block)}>
              <h1 className={css(styles.header)}>Download</h1>

              <div className={css(styles.fields)}>
                {this.renderDownloadLinks()}
              </div>
            </div>
          )}

        <div className={css(styles.block)}>
          <h1 className={css(styles.header)}>Metadata</h1>

          <div className={css(styles.fields)}>
            {this.renderFieldName()}
            {this.renderDate()}
            {this.renderMakeModel()}
            {this.renderSoftware()}
            {this.renderDimensions()}
            {this.renderSize()}
            {this.renderLatitude()}
            {this.renderLongitude()}
            {this.renderAccuracy()}
            {this.renderAltitude()}
            {this.renderDirection()}
            {this.renderDistance()}
            {this.renderLabels()}
            {this.renderText()}
          </div>
        </div>
      </div>
    );
  }

  renderMakeModel() {
    const { metadata } = this.props;

    let value = null;

    if (metadata.exif?.make || metadata.exif?.model) {
      value = [metadata.exif.make, metadata.exif.model].join(' ');
    }

    return this.renderField('Make/Model:', value);
  }

  renderSoftware() {
    const { metadata } = this.props;

    let value = null;

    if (metadata.exif?.software != null) {
      value = metadata.exif.software;
    }

    return this.renderField('Software:', value);
  }

  renderDimensions() {
    const { exif } = this.props.metadata;

    let value = null;

    if (exif?.pixel_x_dimension != null && exif?.pixel_y_dimension != null) {
      value = `${exif.pixel_x_dimension} x ${exif.pixel_y_dimension} (${((+exif.pixel_x_dimension * +exif.pixel_y_dimension) / 1000000).toFixed(1)}MP)`;
    }

    return this.renderField('Dimensions:', value);
  }

  renderSize() {
    const fileSize = this.props.metadata.file_size;

    let value = null;

    if (fileSize != null) {
      value = filesize(+fileSize, { round: 0 });
    }

    return this.renderField('Size:', value);
  }

  renderFieldName() {
    const { label } = this.props.item.mediaValue?.element || '';
    return this.renderField('Field name:', label);
  }

  renderDate() {
    const { exif } = this.props.metadata;

    let value = null;

    const timestamp = exif?.date_time_original || exif?.date_time;

    // The timestamp in the exif is in *local* time, despite it being
    // returned in a format that looks like a UTC timestamp. There is an issue
    // with the way the API returns the timestamp as JSON. Do not attempt to
    // use a fancy library to format this without making sure the output is
    // correct - it will probably not get it right by default because of this.
    if (timestamp != null) {
      const [date, fullTime] = timestamp.split('T');
      const time = fullTime ? fullTime.split('.')[0] : '';

      value = `${date} ${time}`;
    }

    return this.renderField('Date:', value);
  }

  renderLatitude() {
    let value = null;

    if (this.props.metadata.latitude != null && this.props.metadata.latitude !== 0) {
      value = this.props.metadata.latitude.toFixed(6);
    }

    return this.renderField('Latitude:', value);
  }

  renderLongitude() {
    let value = null;

    if (this.props.metadata.longitude != null && this.props.metadata.longitude !== 0) {
      value = this.props.metadata.longitude.toFixed(6);
    }

    return this.renderField('Longitude:', value);
  }

  renderAccuracy() {
    const { exif } = this.props.metadata;

    const dop = exif?.gps_dop;

    let value = null;

    if (dop) {
      value = `${(+dop).toFixed(1)}m`;
    }

    return this.renderField('Accuracy:', value);
  }

  renderAltitude() {
    const { exif } = this.props.metadata;

    const altitude = exif?.gps_altitude;

    let value = null;

    if (altitude) {
      value = `${(+altitude).toFixed(1)}m`;
    }

    return this.renderField('Altitude:', value);
  }

  renderDirection() {
    const { exif } = this.props.metadata;

    const direction = exif?.gps_img_direction;

    let value = null;

    if (direction != null && !Number.isNaN(+direction)) {
      value = `${(+direction).toFixed(0)}°`;
    }

    return this.renderField('Direction:', value);
  }

  renderDistance() {
    const { latitude } = this.props.metadata;
    const { longitude } = this.props.metadata;

    const hasFeatureCoordinate = this.props.feature && this.props.feature.hasCoordinate;

    let value = null;

    if (hasFeatureCoordinate && latitude != null && latitude !== 0 && longitude != null && longitude !== 0) {
      const photoPoint = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
      };

      const recordPoint = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [this.props.feature.longitude, this.props.feature.latitude],
        },
      };

      const units = 'kilometers';

      const dist = distance(photoPoint, recordPoint, units) * 1000;

      value = `${dist.toFixed(1)}m`;
    }

    return this.renderField('Distance:', value);
  }

  renderLabels() {
    const { labels } = this.props.metadata;

    if (!labels) {
      return;
    }

    const labelValues = labels.labels.map((object) => object.name);

    return this.renderField('Labels:', labelValues.join(', '));
  }

  renderText() {
    const { text } = this.props.metadata;

    if (!text) {
      return;
    }

    const textValues = text.text_detections.map((object) => object.detected_text);

    return this.renderField('Text:', textValues.join(', '));
  }

  renderField(label, textValue) {
    const { styles } = PhotoMetadataView;

    const value = textValue || 'Unavailable';

    return (
      <div className={css(styles.field)}>
        <label className={css(styles.label)}>{label}</label>
        <div className={css(styles.value)}>{value}</div>
      </div>
    );
  }

  renderDownloadLinks() {
    const { styles } = PhotoMetadataView;

    return (
      <div>
        <div className={css(styles.downloadLinkContainer)}>
          <a
            href={this.props.metadata.original}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.downloadLink)}
          >
            Original
          </a>
        </div>
        <div className={css(styles.downloadLinkContainer)}>
          <a
            href={this.props.metadata.large}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.downloadLink)}
          >
            Large
          </a>
        </div>
        <div className={css(styles.downloadLinkContainer)}>
          <a
            href={this.props.metadata.thumbnail}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.downloadLink)}
          >
            Thumbnail
          </a>
        </div>
      </div>
    );
  }

  renderShareLink() {
    if (!this.context.params.data_share_id) {
      return null;
    }

    const url = `${globalThis.location.origin}/shares/${this.context.params.data_share_id}/photos/${this.props.item.mediaID}/share`;

    return (
      <ShareLink url={url} />
    );
  }
}

const styles = cssInJS({
  container: {
    fontFamily: THEME.fontFamily,
    backgroundColor: '#fff',
    borderLeft: '1px solid #dbdbdb',
  },

  block: {
    padding: 20,
    borderBottom: '1px solid #dbdbdb',
    fontSize: 13,
  },

  header: {
    fontWeight: 'bold',
    marginBottom: 4,
    fontSize: '100%',
  },

  fields: {
    paddingTop: 8,
  },

  field: {
    padding: '3px 0',
  },

  label: {
    display: 'inline-block',
    fontSize: 12,
    width: '40%',
    fontWeight: 'bold',
    verticalAlign: 'top',
    marginBottom: 0,
    lineHeight: '12px',
  },

  value: {
    display: 'inline-block',
    fontSize: 12,
    width: '60%',
    verticalAlign: 'top',
    lineHeight: '12px',
  },

  downloadLinkContainer: {
    marginBottom: 8,
  },

  downloadLink: {
    color: THEME.linkColor,
    textDecoration: 'none',
  },
});

PhotoMetadataView.styles = styles;
